import { createClient, SupabaseClient } from '@supabase/supabase-js'

/**
 * Supabase Client Service
 * Configures and exports the Supabase client for the frontend.
 * Uses singleton pattern to prevent multiple instances during HMR.
 *
 * Environment Variables:
 * - VITE_SUPABASE_URL: Supabase project URL
 * - VITE_SUPABASE_ANON_KEY: Supabase anonymous/public key
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. ' +
    'Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set.'
  )
}

// Use global to persist client across HMR in development
const globalForSupabase = globalThis as typeof globalThis & {
  supabaseClient?: SupabaseClient
}

const existingClient = globalForSupabase.supabaseClient
if (existingClient) {
  console.log('[Supabase] Reusing existing client instance')
} else {
  console.log('[Supabase] Creating new client instance')
}

export const supabase = existingClient ?? createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    debug: false,
    // Disable Navigator Locks to prevent lock contention. Every PostgREST request
    // calls getSession() which acquires the lock; if auth init or token refresh
    // holds the lock too long (cold start, slow network, StrictMode double-mount),
    // all requests queue up and abort with AbortError. Cross-tab auth sync is
    // already handled by Supabase's BroadcastChannel, so locks are not needed.
    lock: async <R>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> => await fn(),
  },
})

// Store in global for HMR persistence
if (import.meta.env.DEV) {
  globalForSupabase.supabaseClient = supabase
}

/**
 * Get the current authenticated user
 */
export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error) throw error
  return user
}

/**
 * Get the current session
 */
export async function getSession() {
  const { data: { session }, error } = await supabase.auth.getSession()
  if (error) throw error
  return session
}

/**
 * Sign out the current user
 * Uses scope: 'local' to avoid network hang waiting for server revocation.
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut({ scope: 'local' })
  if (error) throw error
}

/**
 * Subscribe to auth state changes
 */
export function onAuthStateChange(
  callback: (event: string, session: unknown) => void
) {
  return supabase.auth.onAuthStateChange(callback)
}

/**
 * Verify user's password by attempting sign in
 * Useful for confirming identity before sensitive operations
 */
export async function verifyPassword(email: string, password: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })
  if (error) throw error
}
